import { UserDataDisplay } from "#src/components/Common/UserDataDisplay";
import { useListUsers } from "#src/components/hooks/adapters/useUsers";
import {
  type useTableSortingAndPagination,
  type useTablesSortingAndPagination,
} from "#src/components/Redux/reducers/tableStateReducer";
import {
  useAuthenticatedContext,
  useHasPermission,
  useIsFeatureEnabled,
} from "#src/contexts/AuthenticatedContext.helpers";
import {
  extractTableColumnConfigurationFromTableConfiguration,
  type TableUserInterfaceConfigurationFullType,
  type TableUserInterfaceConfigurationType,
} from "#src/contexts/DefaultUserInterfaceConfiguration.helpers";
import { useStickyState } from "#src/hooks/useStickyState";
import { extractNameFromStorageKey } from "#src/hooks/useStorageKey";
import { ExceptionUtils } from "#src/utils/exception";
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from "@tanstack/react-query";
import {
  DataTable,
  getDataTableGroupByValueAndKeyForGroupByEntry,
  useToast,
  type ConfiguredDataTable,
  type DataTablePanelProps,
  type DataTableProps,
  type GroupingType,
  type HeaderType,
  type ItemBaseType,
} from "@validereinc/common-components";
import {
  DomainAdapterError,
  SortDirection,
  UserInterfaceConfigurationAdapter,
  type FilterValueType,
  type GetListRequestType,
  type GetListResponseType,
  type GroupedByEntryType,
  type UserType,
} from "@validereinc/domain";
import {
  splitPreservingUUIDs,
  StringValueSchema,
  ValidationError,
} from "@validereinc/utilities";
import React, { useMemo, useState } from "react";

/**
 * Hook up a given key to a table UI configuration and provide utilities to
 * interact with it
 * @param storageKey - The storage key for the table configuration
 * @returns utilities to interact with the table UI configuration
 */
export const useDataTableUserInterfaceConfiguration = (storageKey: string) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const {
    isLoading: isAuthenticatedContextLoading,
    v2: {
      userInfo: { user },
      companyInfo: { company },
    },
  } = useAuthenticatedContext();
  const [areUIConfigsEnabled, isLoadingAreUIConfigsEnabled] =
    useIsFeatureEnabled("core:user_interface_configurations");
  const [readUIConfigsAvailable, isLoadingReadUIConfigsAvailable] =
    useHasPermission("user_interface_configurations:read");
  const [saveUIConfigsAvailable, isLoadingSaveUIConfigsAvailable] =
    useHasPermission("user_interface_configurations:write");
  const [deleteUIConfigsAvailable, isLoadingDeleteUIConfigsAvailable] =
    useHasPermission("user_interface_configurations:delete");
  const [_, setConfiguredTable] = useStickyState<ConfiguredDataTable>(
    {
      source: "code",
      headers: [],
    },
    storageKey
  );
  const nameFromStorageKey = useMemo(
    () =>
      splitPreservingUUIDs(
        extractNameFromStorageKey(storageKey, user?.id, company?.id)
      ),
    [storageKey, user?.id, company?.id]
  );

  const matchingDefaultUserInterfaceConfigurationsPayload: Parameters<
    typeof UserInterfaceConfigurationAdapter.getList
  >[0] = {
    sortBy: "updated_at",
    sortDirection: "desc",
    filters: {
      // @ts-expect-error filter typing not great
      key: { $all: nameFromStorageKey },
      company_id: company?.id,
      scope: "component",
      visibility: "company",
      status: "active",
      $or: [{ is_default: true, type: "table" }, { type: "table-default" }],
      isAlreadyFormatted: true,
    },
  };

  const matchingDefaultTableUserInterfaceConfigurationsQuery = useQuery({
    queryKey: [
      "ui-configs",
      "default",
      matchingDefaultUserInterfaceConfigurationsPayload,
    ],
    queryFn: () =>
      UserInterfaceConfigurationAdapter.getList(
        matchingDefaultUserInterfaceConfigurationsPayload
      ),
    enabled:
      areUIConfigsEnabled &&
      readUIConfigsAvailable &&
      !!user?.id &&
      !!company?.id,
    staleTime: 1000 * 60 * 30,
  });

  // the latest default config for the given storage key is the target config
  const matchingDefaultConfiguration =
    matchingDefaultTableUserInterfaceConfigurationsQuery.data?.data[0] as
      | TableUserInterfaceConfigurationFullType
      | undefined;

  const saveDefaultConfigurationMutation = useMutation({
    mutationFn: async ({
      configuration,
      name,
      status,
      visibility,
    }: {
      configuration: TableUserInterfaceConfigurationType;
    } & Pick<
      Parameters<typeof UserInterfaceConfigurationAdapter.updateOne>[0]["data"],
      "name" | "status" | "visibility"
    >) => {
      if (matchingDefaultConfiguration) {
        return UserInterfaceConfigurationAdapter.updateOne({
          id: matchingDefaultConfiguration.id,
          data: {
            ...(name ? { name } : {}),
            ...(status ? { status } : { status: "active" }),
            ...(visibility ? { visibility } : { visibility: "company" }),
            is_default: true,
            configuration,
          },
        });
      }

      return UserInterfaceConfigurationAdapter.createOne({
        data: {
          ...(name
            ? { name }
            : { name: `Default table configuration for ${storageKey}` }),
          ...(status ? { status } : { status: "active" }),
          ...(visibility ? { visibility } : { visibility: "company" }),
          type: "table",
          is_default: true,
          key: splitPreservingUUIDs(
            extractNameFromStorageKey(storageKey, user?.id, company?.id)
          ),
          configuration,
        },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["ui-configs", "default"],
      });
      toast.push({
        intent: "success",
        description: "Successfully saved table column configuration",
      });
    },
    onError: (err, vars) => {
      const normalizedError = new DomainAdapterError(
        `Failed to save table column configuration for ${storageKey}:`,
        {
          cause: err,
          getExplanation: () => "Failed to save table column configuration.",
          adapterCalls: ["UserInterfaceConfigurationAdapter.updateOne"],
          adapterCallData: [vars],
          props: {
            storageKey,
          },
        }
      );
      toast.push({
        intent: "error",
        description: normalizedError.getExplanation(),
      });
      ExceptionUtils.reportException(
        normalizedError,
        normalizedError.severity,
        {
          sourceComponent: "useTableSavedConfiguration",
        }
      );
    },
  });

  const resetConfigurationMutation = useMutation({
    mutationFn: async () => {
      if (!matchingDefaultConfiguration) {
        window.localStorage.removeItem(storageKey);
        return;
      }

      const cols = extractTableColumnConfigurationFromTableConfiguration(
        matchingDefaultConfiguration.configuration
      );

      if (!cols) {
        throw new ValidationError(
          "Found default table configuration but there are no columns configured in it."
        );
      }

      setConfiguredTable({
        headers: cols,
        source: "user-default",
      });

      return cols;
    },
    onSuccess: (data) => {
      toast.push({
        intent: "success",
        description: `Successfully reset table column configuration${data ? ` to available default` : ""}.`,
      });
    },
    onError: (err) => {
      const normalizedError = new DomainAdapterError(
        `Failed to reset table column configuration for ${storageKey}:`,
        {
          cause: err,
          getExplanation: () => "Failed to reset table column configuration.",
          props: {
            storageKey,
          },
        }
      );
      ExceptionUtils.reportException(normalizedError, "warning", {
        sourceComponent: "useTableSavedConfiguration",
      });
    },
  });

  const deleteDefaultConfigurationMutation = useMutation({
    mutationFn: async () => {
      if (!matchingDefaultConfiguration)
        return { success: false, error: "No default configuration found" };

      return UserInterfaceConfigurationAdapter.deleteOne({
        id: matchingDefaultConfiguration.id,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["ui-configs", "default"],
      });
      toast.push({
        intent: "success",
        description:
          "Successfully deleted saved default table column configuration.",
      });
    },
    onError: (err) => {
      const normalizedError = new DomainAdapterError(
        `Could not delete UI configuration ${matchingDefaultConfiguration?.id}:`,
        {
          cause: err,
          getExplanation: () =>
            "Failed to delete saved default table column configuration.",
          adapterCalls: ["UserInterfaceConfigurationAdapter.deleteOne"],
          adapterCallData: [
            {
              id: matchingDefaultConfiguration?.id,
            },
          ],
        }
      );
      ExceptionUtils.reportException(normalizedError, "warning", {
        sourceComponent: "useTableSavedConfiguration",
      });
    },
  });

  return {
    /** are all these utilities ready to use? */
    isLoading:
      isAuthenticatedContextLoading ||
      isLoadingAreUIConfigsEnabled ||
      isLoadingReadUIConfigsAvailable ||
      isLoadingDeleteUIConfigsAvailable ||
      isLoadingSaveUIConfigsAvailable,
    /** save a default configuration with the given key */
    saveDefaultConfigurationMutation,
    /** check if saving is available */
    isSaveConfigurationAvailable: saveUIConfigsAvailable,
    /** reset the configuration with the given key */
    resetConfigurationMutation,
    /** delete the default configuration with the given key */
    deleteDefaultConfigurationMutation,
    /** check if deleting is available */
    isDeleteConfigurationAvailable: deleteUIConfigsAvailable,
    /** get the default configuration with the given key */
    matchingDefaultConfiguration,
    /** the query to get the default configuration with the given key */
    matchingDefaultTableUserInterfaceConfigurationsQuery,
  };
};

/**
 * Hook that makes it easy to provide column configuration props to a
 * DataTablePanel component
 */
export const useDataTableColumnConfigurationProps = ({
  configurationName,
  saveDefaultConfigurationMutation,
  isSaveConfigurationAvailable,
  resetConfigurationMutation,
  deleteDefaultConfigurationMutation,
  isDeleteConfigurationAvailable,
  isLoading,
  matchingDefaultConfiguration,
}: ReturnType<typeof useDataTableUserInterfaceConfiguration> & {
  configurationName: string;
}) => {
  return {
    onSaveColumnConfigurationAsDefault: (colsToSave) =>
      saveDefaultConfigurationMutation
        .mutateAsync({
          name: configurationName,
          configuration: {
            columns: colsToSave,
          },
        })
        .then(() => true)
        .catch(() => false),
    isLoadingSaveDefaultColumnConfiguration:
      saveDefaultConfigurationMutation.isLoading,
    isSaveDefaultColumnConfigurationAvailable: isSaveConfigurationAvailable,
    onResetColumnConfiguration: () => resetConfigurationMutation.mutateAsync(),
    onDeleteDefaultColumnConfiguration: () =>
      deleteDefaultConfigurationMutation
        .mutateAsync()
        .then(() => true)
        .catch(() => false),
    isLoadingDeleteDefaultColumnConfiguration:
      deleteDefaultConfigurationMutation.isLoading,
    isDeleteDefaultColumnConfigurationAvailable: isDeleteConfigurationAvailable,
    isLoadingDefaultColumnConfiguration: isLoading,
    isDefaultColumnConfigurationAvailable: !!matchingDefaultConfiguration,
    defaultColumnConfiguration:
      matchingDefaultConfiguration?.configuration?.columns,
  };
};

/**
 * Hook that returns utilities to enable group by functionality in a
 * DataTablePanel
 * @param data - grouped by data
 * @param groupBy - grouped by fields
 * @param payloads - payloads to use for the group by queries
 * @returns utilities to enable group by functionality in a DataTablePanel
 */
export const useDataTableGroupBy = <
  TValue = unknown,
  TAdditionalGroupByFields extends object = Record<string, never>,
>(
  data: Array<GroupedByEntryType<TValue, TAdditionalGroupByFields>>,
  groupBy: string[],
  payloads: GetListRequestType[],
  getGroupByFilterValue?: (
    groupedEntry: GroupedByEntryType<TValue, TAdditionalGroupByFields>
  ) => FilterValueType
) => {
  const [activeGroupByGroups, setActiveGroupByGroups] = useState<string[]>(
    () =>
      groupBy.length && data[0]
        ? [
            getDataTableGroupByValueAndKeyForGroupByEntry(
              data[0] as unknown as GroupedByEntryType
            )[1],
          ]
        : []
  );

  const totalSelectableItems = useMemo(() => {
    return groupBy.length
      ? data.reduce((tot, item) => {
          return tot + item.count;
        }, 0)
      : null;
  }, [groupBy, data]);

  return {
    /** currently open group by groups in the panel */
    activeGroupByGroups,
    /** an associated setter */
    setActiveGroupByGroups,
    /** the total number of selectable items across all groups */
    totalSelectableItems,
    /** a list of query payloads for each group in data */
    queryPayloads: !groupBy.length
      ? []
      : data.map((groupedEntry, idx) => {
          const key = getDataTableGroupByValueAndKeyForGroupByEntry(
            groupedEntry as unknown as GroupedByEntryType
          )[1];

          return {
            key,
            groupByValue: groupedEntry.value,
            payload: {
              page: 1,
              pageSize: 10,
              ...(payloads[idx] ?? {}),
              groupBy: undefined,
              filters: {
                ...(payloads[idx]?.filters ?? {}),
                // this filter is key to returning the right data for this group
                [groupBy[0]]:
                  getGroupByFilterValue !== undefined
                    ? getGroupByFilterValue(groupedEntry)
                    : { $exact: groupedEntry.value },
              },
            },
            enabled:
              activeGroupByGroups.includes(key) ||
              (typeof groupedEntry.value === "string" &&
                StringValueSchema.uuid().safeParse(groupedEntry.value).success),
          };
        }),
  };
};

/**
 * Hook that makes it easy to provide group by props to a DataTablePanel
 * component
 */
export const useDataTableGroupByProps = <ItemType extends ItemBaseType>({
  items,
  groupBy,
  updateTableState,
  setActiveGroupByGroups,
  groupByQueries,
  groupByTablesStates,
  updateGroupByTablesStates,
  headers,
  getItemActions,
  getItemBulkActions,
  getItemId,
  selected,
  onSelectionChange,
  totalSelectableItems,
}: {
  groupBy: GroupingType["groupBy"];
  updateTableState: ReturnType<typeof useTableSortingAndPagination>[1];
  setActiveGroupByGroups: ReturnType<
    typeof useDataTableGroupBy
  >["setActiveGroupByGroups"];
  groupByQueries: Array<UseQueryResult<GetListResponseType<ItemType>>>;
  groupByTablesStates: ReturnType<typeof useTablesSortingAndPagination>[0];
  updateGroupByTablesStates: ReturnType<
    typeof useTablesSortingAndPagination
  >[1];
  totalSelectableItems: ReturnType<
    typeof useDataTableGroupBy
  >["totalSelectableItems"];
} & Pick<
  DataTableProps<ItemType>,
  | "items"
  | "headers"
  | "getItemActions"
  | "getItemBulkActions"
  | "getItemId"
  | "selected"
  | "onSelectionChange"
>): Pick<
  DataTablePanelProps<ItemType>,
  | "groupBy"
  | "onGroupByChange"
  | "groupByLimit"
  | "onActiveGroupByGroupsChange"
  | "defaultActiveGroupByGroups"
  | "groupByTablesProps"
> => {
  const firstGroupKey = useMemo(() => {
    return items.length && groupBy.length
      ? getDataTableGroupByValueAndKeyForGroupByEntry(
          items[0] as unknown as GroupedByEntryType
        )[1]
      : "-";
  }, [JSON.stringify(items[0])]);

  const memoizedTablesProps = useMemo(() => {
    return groupByQueries.map<DataTableProps<ItemType>>((query, idx) => {
      const matchingTableState = groupByTablesStates.get(
        idx === 0
          ? firstGroupKey
          : getDataTableGroupByValueAndKeyForGroupByEntry(
              items[idx] as unknown as GroupedByEntryType
            )[1]
      );

      return {
        items: query.data?.data ?? [],
        isLoading: query.isLoading,
        isBusy: query.isFetching,
        headers,
        getItemActions,
        getItemBulkActions,
        getItemId,
        selected,
        onSelectionChange,
        totalSelectableItems: totalSelectableItems ?? undefined,
        intent: "simplicity-first",
        sorting: {
          sortBy: matchingTableState?.sortBy ?? "updated_at",
          sortDirection:
            matchingTableState?.sortDirection ?? SortDirection.DESCENDING,
        },
        onSortChange: (newSortState) =>
          updateGroupByTablesStates(groupByTablesStates, {
            type: "update",
            key:
              idx === 0
                ? firstGroupKey
                : getDataTableGroupByValueAndKeyForGroupByEntry(
                    items[idx] as unknown as GroupedByEntryType
                  )[1],
            tableState: newSortState,
          }),
        pagination: {
          page: matchingTableState?.page ?? 1,
          pageSize: matchingTableState?.pageSize ?? 10,
          total: query.data?.total_entries ?? 0,
        },
        onPaginationChange: (newPaginationState) =>
          updateGroupByTablesStates(groupByTablesStates, {
            type: "update",
            key:
              idx === 0
                ? firstGroupKey
                : getDataTableGroupByValueAndKeyForGroupByEntry(
                    items[idx] as unknown as GroupedByEntryType
                  )[1],
            tableState: newPaginationState,
          }),
      };
    });
  }, [
    groupByQueries,
    headers,
    items,
    groupByTablesStates,
    getItemActions,
    firstGroupKey,
  ]);

  return {
    groupBy,
    onGroupByChange: updateTableState,
    onActiveGroupByGroupsChange: setActiveGroupByGroups,
    groupByTablesProps: memoizedTablesProps,
    defaultActiveGroupByGroups: items.length ? [firstGroupKey] : [],
  };
};

export const useDataTableMetaHeaders = <ItemType extends ItemBaseType>(
  opts: {
    updatedByLabel?: string;
    createdByLabel?: string;
  } = {}
): Array<HeaderType<ItemType>> => {
  return useMemo(
    () => [
      {
        key: "updated_at",
        label: opts.updatedByLabel || "Updated On",
        isSelected: true,
        isSortable: true,
        // REVIEW: grouping on dates is not great right now because granularity is not controllable
        isGroupable: false,
        renderComponent: ({ item, isLoading, opts }) => (
          <DataTable.DataRow.DateCell
            value={item.updated_at}
            withTime
            convertToUTC={false}
            isLoading={isLoading}
            {...opts}
          />
        ),
      },
      {
        key: "created_at",
        label: opts.createdByLabel || "Created On",
        isSelected: false,
        isSortable: true,
        // REVIEW: grouping on dates is not great right now because granularity is not controllable
        isGroupable: false,
        renderComponent: ({ item, isLoading, opts }) => (
          <DataTable.DataRow.DateCell
            value={item.created_at}
            withTime
            convertToUTC={false}
            isLoading={isLoading}
            {...opts}
          />
        ),
      },
    ],
    []
  );
};

export const useDataTableMetaExtendedHeaders = <ItemType extends ItemBaseType>(
  items: ItemType[],
  opts: {
    updatedByLabel?: string;
    createdByLabel?: string;
  } = {}
): Array<HeaderType<ItemType>> => {
  const metaUserQuery = useListUsers({
    filters: {
      id: Array.from(
        new Set(
          items.flatMap((item) => [
            item.created_by ?? "",
            item.updated_by ?? "",
          ])
        )
      ),
    },
  });

  const metaUserQueryMap = useMemo(() => {
    return metaUserQuery.data?.data.reduce<Record<string, UserType>>(
      (map, data) => {
        map[data.id] = data;
        return map;
      },
      {}
    );
  }, [metaUserQuery.data?.data]);

  return useMemo(
    () => [
      {
        key: "updated_by",
        label: opts.updatedByLabel || "Updated By",
        isSelected: true,
        isSortable: true,
        isGroupable: true,
        renderComponent: ({ item, isLoading, opts }) =>
          item.updated_by ? (
            <UserDataDisplay
              id={item.updated_by}
              name={metaUserQueryMap?.[item.updated_by]?.name ?? "n/a"}
              isLoading={isLoading || metaUserQuery.isLoading}
              {...opts}
            />
          ) : (
            "-"
          ),
      },
      {
        key: "created_by",
        label: opts.createdByLabel || "Created By",
        isSelected: false,
        isSortable: true,
        isGroupable: true,
        renderComponent: ({ item, isLoading, opts }) =>
          item.created_by ? (
            <UserDataDisplay
              id={item.created_by}
              name={metaUserQueryMap?.[item.created_by]?.name ?? "n/a"}
              isLoading={isLoading || metaUserQuery.isLoading}
              {...opts}
            />
          ) : (
            "-"
          ),
      },
    ],
    []
  );
};
